import styled from '@emotion/styled';
import { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Button, ToggleButton } from '@innovamat/glimmer-components';
import { usePrint, useQueryParams } from '@innovamat/hooks';

import {
  useCurrentNavigationValues,
  useResourceParams,
  useStandard,
} from '../../hooks';
import { moveContentToPrint } from './move-content-to-print';
import { PrintGuide } from './print-guide';

const Wrapper = styled.div`
  padding: 24px;
  width: 100%;
  height: var(--realVh);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  padding-bottom: 58px;

  @page {
    margin: 12.5mm;
  }
`;

const BreakPage = styled.div`
  page-break-after: always;
`;

const PrintSection = styled.div`
  background-color: white;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.2);
  border-top: 1px solid #e5e5e5;
  padding: 8px;
  width: 280px;
  position: fixed;
  bottom: 10px;
  gap: 4px;
  left: 50%;
  border-radius: 8px;
  transform: translateX(-50%);
  z-index: 20;
  display: flex;
  flex-direction: column;
  text-align: center;
  align-items: center;
`;

const Disclaimer = styled.div<{ show: boolean }>`
  font-size: 12px;
  color: #888;

  max-height: ${({ show }) => (show ? '100px' : '0px')};
  overflow: hidden;
  transition: max-height 0.3s ease-in-out;
`;

type Props = {
  onPrint: () => void;
  loading: boolean;
};

export function PrintFooter({ onPrint, loading }: Props) {
  const [showDisclaimer, setShowDisclaimer] = useState(false);

  return (
    <PrintSection
      onMouseEnter={() => setShowDisclaimer(true)}
      onMouseLeave={() => setShowDisclaimer(false)}
      id="print-footer"
    >
      <>
        <Button fill onClick={onPrint} variant="accent" loading={loading}>
          Imprimir
        </Button>
        <Disclaimer show={showDisclaimer}>
          Recuerda modificar las opciones para los márgenes y la leyenda a la
          hora de imprimir
        </Disclaimer>
      </>
    </PrintSection>
  );
}

const LegendContainer = styled.div`
  position: fixed;
  bottom: 10px;
  left: 10px;
  background-color: white;
  padding: 16px;
  border-radius: 8px;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.2);
  z-index: 20;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const LegendTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 4px;
`;

function Legend({
  isEditMode,
  setIsEditMode,
}: {
  isEditMode: boolean;
  setIsEditMode: (isEditMode: boolean) => void;
}) {
  return (
    <LegendContainer>
      <LegendTitle>Manual Mode</LegendTitle>
      <ToggleButton
        checked={isEditMode}
        onChange={() => setIsEditMode(!isEditMode)}
      />
    </LegendContainer>
  );
}

export function PrintGuides() {
  const { ids } = useParams();
  const params = useQueryParams();
  const hasBreakPage = !!params.get('breakPage') as boolean;
  const { componentRef, onPrint, loading } = usePrint('Smart Guides');

  const { region, language } = useResourceParams();
  const { courseOrder } = useCurrentNavigationValues();

  const isContentMoved = useRef(false);
  const idsArray = ids?.split(',');

  const [isEditMode, setIsEditMode] = useState(false);

  const { standard } = useStandard();

  const handlePrint = (): void => {
    if (!hasBreakPage || isContentMoved.current || isEditMode) {
      onPrint();
    } else {
      moveContentToPrint();
      isContentMoved.current = true;
      setTimeout(() => {
        onPrint();
      }, 500);
    }
  };

  return (
    <Wrapper>
      <PrintFooter onPrint={handlePrint} loading={loading} />
      <Legend isEditMode={isEditMode} setIsEditMode={setIsEditMode} />
      <div ref={componentRef}>
        {idsArray?.map((id: string) => (
          <PrintGuide
            id={id}
            hasBreakPage={hasBreakPage}
            isEditMode={isEditMode}
            language={language}
            region={region}
            courseOrder={courseOrder}
            standard={standard}
          />
        ))}
      </div>
    </Wrapper>
  );
}
